import { Check, Globe, Moon, Sun } from "lucide-react";
import { format } from "date-fns";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../card";
import { Label } from "../label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";

export type ThemeOption = "light" | "dark" | "system";
export type DateFormat = "MM/dd/yyyy" | "dd/MM/yyyy" | "yyyy-MM-dd";

const THEME_OPTIONS = [
  {
    value: "light" as ThemeOption,
    label: "Light",
    icon: Sun,
    description: "Clean layout for bright environments.",
  },
  {
    value: "dark" as ThemeOption,
    label: "Dark",
    icon: Moon,
    description: "Easy on the eyes in low light.",
  },
  {
    value: "system" as ThemeOption,
    label: "System",
    icon: Globe,
    description: "Matches your system settings.",
  },
];

const DATE_FORMAT_OPTIONS: Array<{ value: DateFormat; label: string }> = [
  { value: "MM/dd/yyyy", label: "Month/Day/Year" },
  { value: "dd/MM/yyyy", label: "Day/Month/Year" },
  { value: "yyyy-MM-dd", label: "Year-Month-Day" },
];

const formatDateExample = (formatString: string): string => {
  return format(new Date(2025, 2, 28), formatString);
};

function ThemeMockup({ mode }: { mode: "light" | "dark" | "system" }) {
  if (mode === "light") {
    return (
      <div className="relative flex aspect-[16/10] w-full flex-col gap-1.5 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 p-2 shadow-xs select-none">
        <div className="flex items-center gap-1 border-b border-zinc-200 pb-1">
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-200" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-200" />
        </div>
        <div className="flex flex-1 gap-2 overflow-hidden">
          <div className="flex w-1/4 flex-col gap-1 rounded bg-zinc-200/50 p-1">
            <div className="h-1.5 w-full rounded-xs bg-zinc-300/60" />
            <div className="h-1 w-3/4 rounded-xs bg-zinc-300/40" />
            <div className="h-1 w-5/6 rounded-xs bg-zinc-300/40" />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="flex flex-1 flex-col gap-1 rounded border border-zinc-200/50 bg-white p-1.5">
              <div className="h-1.5 w-1/2 rounded-xs bg-zinc-300/60" />
              <div className="h-1 w-3/4 rounded-xs bg-zinc-200/80" />
              <div className="h-1 w-full rounded-xs bg-zinc-200/80" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "dark") {
    return (
      <div className="relative flex aspect-[16/10] w-full flex-col gap-1.5 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 p-2 shadow-xs select-none">
        <div className="border-zinc-850 flex items-center gap-1 border-b pb-1">
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-700" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-800" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-800" />
        </div>
        <div className="flex flex-1 gap-2 overflow-hidden">
          <div className="border-zinc-850 flex w-1/4 flex-col gap-1 rounded border bg-zinc-900 p-1">
            <div className="h-1.5 w-full rounded-xs bg-zinc-800/80" />
            <div className="bg-zinc-850/50 h-1 w-3/4 rounded-xs" />
            <div className="bg-zinc-850/50 h-1 w-5/6 rounded-xs" />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="border-zinc-850 flex flex-1 flex-col gap-1 rounded border bg-zinc-900 p-1.5">
              <div className="h-1.5 w-1/2 rounded-xs bg-zinc-800/80" />
              <div className="bg-zinc-850/80 h-1 w-3/4 rounded-xs" />
              <div className="bg-zinc-850/80 h-1 w-full rounded-xs" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dark:border-zinc-850 relative flex aspect-[16/10] w-full overflow-hidden rounded-lg border border-zinc-200 shadow-xs select-none">
      <div className="flex w-1/2 flex-col gap-1.5 border-r border-zinc-200 bg-zinc-50 p-2">
        <div className="flex items-center gap-1 border-b border-zinc-200/60 pb-1">
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-200" />
        </div>
        <div className="flex flex-1 gap-1.5 overflow-hidden">
          <div className="flex w-1/3 flex-col gap-1 rounded bg-zinc-200/50 p-1">
            <div className="h-1.5 w-full rounded-xs bg-zinc-300/60" />
          </div>
          <div className="flex-1 rounded border border-zinc-200/50 bg-white p-1" />
        </div>
      </div>
      <div className="flex w-1/2 flex-col gap-1.5 bg-zinc-950 p-2">
        <div className="border-zinc-850 flex items-center gap-1 border-b pb-1">
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-700" />
          <div className="h-1.5 w-1.5 rounded-full bg-zinc-800" />
        </div>
        <div className="flex flex-1 gap-1.5 overflow-hidden">
          <div className="flex w-1/3 flex-col gap-1 rounded bg-zinc-900 p-1">
            <div className="h-1.5 w-full rounded-xs bg-zinc-800/80" />
          </div>
          <div className="border-zinc-850 flex-1 rounded border bg-zinc-900 p-1" />
        </div>
      </div>
    </div>
  );
}

export interface PreferencesViewProps {
  dateFormat: string;
  isLoading: boolean;
  isSaving: boolean;
  theme: string;
  onThemeChange: (theme: string) => void;
  onDateFormatChange: (format: string) => void;
}

export function PreferencesView({
  dateFormat,
  isLoading,
  isSaving,
  theme,
  onThemeChange,
  onDateFormatChange,
}: PreferencesViewProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="bg-muted h-8 w-40 animate-pulse rounded-md" />
          <div className="bg-muted mt-2 h-4 w-72 animate-pulse rounded-md" />
        </div>
        <Card>
          <CardHeader>
            <div className="bg-muted h-5 w-32 animate-pulse rounded-md" />
            <div className="bg-muted mt-2 h-4 w-60 animate-pulse rounded-md" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="bg-muted aspect-[16/10] animate-pulse rounded-lg" />
              <div className="bg-muted aspect-[16/10] animate-pulse rounded-lg" />
              <div className="bg-muted aspect-[16/10] animate-pulse rounded-lg" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="bg-muted h-5 w-48 animate-pulse rounded-md" />
            <div className="bg-muted mt-2 h-4 w-80 animate-pulse rounded-md" />
          </CardHeader>
          <CardContent>
            <div className="bg-muted h-10 w-full animate-pulse rounded-md sm:max-w-md" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Preferences</h1>
        <p className="text-muted-foreground mt-1.5 text-sm">
          Customize how the application looks and displays information.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Appearance</CardTitle>
          <CardDescription>
            Choose your preferred theme mode for the interface.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {THEME_OPTIONS.map((option) => {
              const isSelected = theme === option.value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onThemeChange(option.value)}
                  disabled={isSaving}
                  className={`group focus-visible:ring-ring relative flex flex-col items-center gap-3 rounded-xl border p-3 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98] ${
                    isSelected
                      ? "border-primary bg-primary/[0.02] ring-primary dark:bg-primary/[0.01] ring-1"
                      : "border-border hover:border-foreground/20 hover:bg-accent/40"
                  }`}
                >
                  <ThemeMockup mode={option.value} />
                  <div className="flex w-full flex-col gap-1 px-1">
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <Icon
                          className={`h-4 w-4 transition-colors ${
                            isSelected
                              ? "text-primary"
                              : "text-muted-foreground group-hover:text-foreground"
                          }`}
                        />
                        {option.label}
                      </span>
                      {isSelected && (
                        <div className="bg-primary animate-in fade-in zoom-in-75 flex h-4 w-4 items-center justify-center rounded-full duration-200">
                          <Check className="text-primary-foreground h-2.5 w-2.5 stroke-[3]" />
                        </div>
                      )}
                    </div>
                    <span className="text-muted-foreground line-clamp-1 text-xs">
                      {option.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            Regional & Format Settings
          </CardTitle>
          <CardDescription>
            Configure how dates, times, and regional formats are displayed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="date-format" className="text-sm font-medium">
              Date Format
            </Label>
            <Select
              value={dateFormat}
              onValueChange={onDateFormatChange}
              disabled={isSaving}
            >
              <SelectTrigger
                id="date-format"
                className="w-full bg-transparent sm:max-w-md"
              >
                <SelectValue placeholder="Select a date format" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMAT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="mr-2 text-sm font-semibold">
                      {formatDateExample(option.value)}
                    </span>
                    <span className="text-muted-foreground text-xs font-normal">
                      ({option.label})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Dates will be displayed in this format across all tables, logs,
              and pages.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
